/**
 * ELT Capabilities System
 *
 * This file defines the unified capabilities system for ELT templates (V1 and V2).
 * It provides a consistent way to detect, process, and manage capabilities across
 * different template versions.
 *
 * Key Concepts:
 * - Capabilities represent features like Multi Schema, Object Detection, Field Selection, etc.
 * - Each capability has different detection logic for V1 vs V2 templates
 * - V1: Capabilities are detected from resource_parameters with specific param_types
 * - V2: Capabilities are detected from capabilities config objects
 * - FEED_STRATEGIES is special: always present, not enabled/disabled
 *
 * Structure:
 * - ELT_CAPABILITIES: Defines all available capabilities and their detection logic
 * - CapabilityChecker: Helper functions to check if capabilities are enabled
 * - extractV1Capabilities: Processes V1 resource_parameters into unified structure
 * - extractV2Capabilities: Processes V2 capabilities config into unified structure
 * - extractV1AdditionalParams/extractV2AdditionalParams: Separates non-capability parameters
 */

export const ELT_CAPABILITIES = {
  // Schema Mode: Single vs Multi Schema
  MULTI_SCHEMA: {
    key: 'multi_schema',
    variant: 'standard', // Standard capability with enabled/disabled
    isUserConfigurable: true, // User can configure this parameter
    v1: {
      // In V1, look for resource param with param_type: "single_multi_schema_selector"
      paramType: 'single_multi_schema_selector',
      paramNamePattern: /\.single_schema$/,
      enabled: (resourceParams: any[]) =>
        resourceParams.some((param) => param.config?.param_type === 'single_multi_schema_selector'),
    },
    v2: {
      // In V2, look for multi_schema capability
      capabilityPath: 'multi_schema.enabled',
      paramNamePath: 'multi_schema.param_name',
      enabled: (capabilities: any) => capabilities?.multi_schema?.enabled === true,
    },
  },

  // Object Detection: Auto-detect new objects
  OBJECT_DETECTION: {
    key: 'object_detection',
    variant: 'standard', // Standard capability with enabled/disabled
    isUserConfigurable: true, // User can configure this parameter
    v1: {
      // In V1, look for resource param with param_type: "object_detection_mode_selector"
      paramType: 'object_detection_mode_selector',
      paramNamePattern: /\.auto_detect_feeds$/,
      enabled: (resourceParams: any[]) =>
        resourceParams.some(
          (param) => param.config?.param_type === 'object_detection_mode_selector',
        ),
    },
    v2: {
      // In V2, look for schema_discovery capability
      capabilityPath: 'schema_discovery.enabled',
      paramNamePath: 'schema_discovery.param_name',
      enabled: (capabilities: any) => capabilities?.schema_discovery?.enabled === true,
    },
  },

  // Field Selection: Force field selection
  FIELD_SELECTION: {
    key: 'field_selection',
    variant: 'standard', // Standard capability with enabled/disabled
    isUserConfigurable: false, // Metadata/rules only, not user configurable
    v1: {
      // In V1, look for resource params with field selection patterns
      paramType: 'field_selector',
      paramNamePattern: /\.(force_fields|field_selector|field_list)/,
      enabled: (resourceParams: any[]) =>
        resourceParams.some(
          (param) =>
            param.config?.param_type === 'field_selector' ||
            param.config?.param_type === 'field_list_selector' ||
            param.config?.param_type === 'field_list' ||
            /\.(force_fields|field_selector|field_list)/.test(param.name),
        ),
    },
    v2: {
      // In V2, look for field_selection capability
      capabilityPath: 'field_selection.enabled',
      paramNamePath: 'field_selection.field_list_selector_param',
      enabled: (capabilities: any) => capabilities?.field_selection?.enabled === true,
    },
  },

  // Lookback Time: Incremental sync lookback
  LOOKBACK_TIME: {
    key: 'lookback_time',
    variant: 'standard', // Standard capability with enabled/disabled
    isUserConfigurable: true, // User can configure this parameter
    v1: {
      // In V1, look for sync_freq parameters
      paramType: 'sync_freq',
      paramNamePattern: /\.sync_freq$/,
      enabled: (resourceParams: any[]) =>
        resourceParams.some((param) => param.name.includes('sync_freq')),
    },
    v2: {
      // In V2, look for lookback_time capability
      capabilityPath: 'lookback_time.enabled',
      paramNamePath: 'lookback_time.param_name',
      enabled: (capabilities: any) => capabilities?.lookback_time?.enabled === true,
    },
  },

  // Feed Strategies: Available sync strategies
  FEED_STRATEGIES: {
    key: 'feed_strategies',
    variant: 'v1_v2_different', // Indicates this capability has different V1 vs V2 variants that components handle differently
    isUserConfigurable: false, // Metadata/rules only, not user configurable
    v1: {
      // In V1, determine strategies from feed_types parameter
      paramType: 'feed_types',
      paramNamePattern: /\.ref_feed_types$/,
      getStrategies: (resourceParams: any[]) => {
        const feedTypesParam = resourceParams.find(
          (param) => param.config?.param_type === 'feed_types',
        );
        return feedTypesParam?.config?.allowed_values || [];
      },
    },
    v2: {
      // In V2, get strategies from capabilities.feed_strategies.supported
      capabilityPath: 'feed_strategies.supported',
      paramNamePath: 'feed_strategies.default',
      getStrategies: (capabilities: any) => capabilities?.feed_strategies?.supported || [],
    },
  },
} as const;

// Type for capability keys
export type CapabilityKey = keyof typeof ELT_CAPABILITIES;

// Helper functions to check capabilities
export const CapabilityChecker = {
  /**
   * Check if a capability is user configurable (should be shown in ResourceParamFields)
   */
  isUserConfigurable: (capability: CapabilityKey): boolean => {
    const cap = ELT_CAPABILITIES[capability];
    return cap.isUserConfigurable === true;
  },

  /**
   * Check if a capability is enabled in V1 template
   */
  isV1CapabilityEnabled: (capability: CapabilityKey, resourceParams: any[]): boolean => {
    const cap = ELT_CAPABILITIES[capability];

    // FEED_STRATEGIES is always present, not enabled/disabled
    if (cap.variant === 'v1_v2_different') {
      return true;
    }

    // Standard capabilities have enabled function
    if (cap.variant === 'standard' && 'enabled' in cap.v1) {
      return cap.v1.enabled(resourceParams);
    }

    return false;
  },

  /**
   * Check if a capability is enabled in V2 template
   */
  isV2CapabilityEnabled: (capability: CapabilityKey, capabilities: any): boolean => {
    const cap = ELT_CAPABILITIES[capability];

    // FEED_STRATEGIES is always present, not enabled/disabled
    if (cap.variant === 'v1_v2_different') {
      return true;
    }

    // Standard capabilities have enabled function
    if (cap.variant === 'standard' && 'enabled' in cap.v2) {
      return cap.v2.enabled(capabilities);
    }

    return false;
  },

  /**
   * Get all enabled capabilities for V1 template
   */
  getV1EnabledCapabilities: (resourceParams: any[]): CapabilityKey[] => {
    return Object.keys(ELT_CAPABILITIES).filter((cap) =>
      CapabilityChecker.isV1CapabilityEnabled(cap as CapabilityKey, resourceParams),
    ) as CapabilityKey[];
  },

  /**
   * Get all enabled capabilities for V2 template
   */
  getV2EnabledCapabilities: (capabilities: any): CapabilityKey[] => {
    return Object.keys(ELT_CAPABILITIES).filter((cap) =>
      CapabilityChecker.isV2CapabilityEnabled(cap as CapabilityKey, capabilities),
    ) as CapabilityKey[];
  },

  /**
   * Get capability info for a specific capability
   */
  getCapabilityInfo: (capability: CapabilityKey) => {
    return ELT_CAPABILITIES[capability];
  },

  /**
   * Get available strategies for FEED_STRATEGIES capability
   */
  getAvailableStrategies: (
    capability: CapabilityKey,
    templateVersion: 'v1' | 'v2',
    config: any,
    resourceParams: any[] = [],
  ): string[] => {
    const cap = ELT_CAPABILITIES[capability];

    if (
      cap.variant === 'v1_v2_different' &&
      templateVersion === 'v1' &&
      'getStrategies' in cap.v1
    ) {
      return cap.v1.getStrategies(resourceParams);
    } else if (
      cap.variant === 'v1_v2_different' &&
      templateVersion === 'v2' &&
      'getStrategies' in cap.v2
    ) {
      return cap.v2.getStrategies(config);
    }

    return [];
  },
};

/**
 * Extract unified capabilities structure from V1 resource_params
 */
export const extractV1Capabilities = (
  resourceParams: any[],
): {
  [capabilityKey: string]: {
    enabled: boolean;
    param_name?: string;
    default?: any;
    params: any[];
    variant: 'v1' | 'v2';
    available_strategies?: string[];
  };
} => {
  const capabilities: {
    [capabilityKey: string]: {
      enabled: boolean;
      param_name?: string;
      default?: any;
      params: any[];
      variant: 'v1' | 'v2';
      available_strategies?: string[];
    };
  } = {};

  // Check each capability
  Object.keys(ELT_CAPABILITIES).forEach((capabilityKey) => {
    const capability = ELT_CAPABILITIES[capabilityKey as CapabilityKey];

    // FEED_STRATEGIES is always present in V1, not enabled/disabled
    const isEnabled =
      capability.variant === 'v1_v2_different' ||
      CapabilityChecker.isV1CapabilityEnabled(capabilityKey as CapabilityKey, resourceParams);

    if (isEnabled) {
      // Find the controlling parameter(s)
      let controllingParams: any[] = [];
      let primaryParamName: string | undefined;
      let primaryDefault: any;

      if (capabilityKey === 'FIELD_SELECTION') {
        // FIELD_SELECTION has multiple parameters in V1
        const fieldListSelectorParam = resourceParams.find(
          (param) => param.config?.param_type === 'field_list_selector',
        );
        const fieldListParam = resourceParams.find(
          (param) => param.config?.param_type === 'field_list',
        );

        if (fieldListSelectorParam) controllingParams.push(fieldListSelectorParam);
        if (fieldListParam) controllingParams.push(fieldListParam);

        primaryParamName = fieldListSelectorParam?.name;
        primaryDefault = fieldListSelectorParam?.config?.default;
      } else {
        // Single parameter for other capabilities
        const controllingParam = resourceParams.find(
          (param) =>
            param.config?.param_type === capability.v1.paramType ||
            capability.v1.paramNamePattern.test(param.name),
        );

        if (controllingParam) {
          controllingParams = [controllingParam];
          primaryParamName = controllingParam.name;
          primaryDefault = controllingParam.config?.default;
        }
      }

      capabilities[capabilityKey] = {
        enabled: true,
        param_name: primaryParamName,
        default: primaryDefault,
        params: controllingParams, // Array of parameters
        variant: 'v1', // Indicate this is V1 variant
        // Add available strategies for FEED_STRATEGIES
        ...(capabilityKey === 'FEED_STRATEGIES' && {
          available_strategies: CapabilityChecker.getAvailableStrategies(
            capabilityKey as CapabilityKey,
            'v1',
            {},
            resourceParams,
          ),
        }),
      };
    } else {
      capabilities[capabilityKey] = {
        enabled: false,
        params: [], // Empty params array for disabled capabilities
        variant: 'v1', // Indicate this is V1 variant
        // Even if not enabled, FEED_STRATEGIES should still have available strategies
        ...(capabilityKey === 'FEED_STRATEGIES' && {
          available_strategies: CapabilityChecker.getAvailableStrategies(
            capabilityKey as CapabilityKey,
            'v1',
            {},
            resourceParams,
          ),
        }),
      };
    }
  });

  return capabilities;
};

/**
 * Extract unified capabilities structure from V2 capabilities config
 */
export const extractV2Capabilities = (
  capabilitiesConfig: any,
  resourceParams: any[] = [],
): {
  [capabilityKey: string]: {
    enabled: boolean;
    param_name?: string;
    default?: any;
    params: any[];
    variant: 'v1' | 'v2';
    available_strategies?: string[];
  };
} => {
  const capabilities: {
    [capabilityKey: string]: {
      enabled: boolean;
      param_name?: string;
      default?: any;
      params: any[];
      variant: 'v1' | 'v2';
      available_strategies?: string[];
    };
  } = {};

  // Check each capability
  Object.keys(ELT_CAPABILITIES).forEach((capabilityKey) => {
    const capability = ELT_CAPABILITIES[capabilityKey as CapabilityKey];
    const isEnabled = CapabilityChecker.isV2CapabilityEnabled(
      capabilityKey as CapabilityKey,
      capabilitiesConfig,
    );

    if (isEnabled) {
      // Get the param name and default from V2 config
      let paramName: string | undefined;
      let defaultValue: any;
      let params: any[] = [];

      switch (capabilityKey) {
        case 'MULTI_SCHEMA':
          defaultValue =
            capabilitiesConfig?.multi_schema?.default === false ||
            capabilitiesConfig?.multi_schema?.default === 'false'
              ? 'false'
              : 'true';
          paramName = capabilitiesConfig?.multi_schema?.param_name;

          params = [
            {
              name: paramName,
              display_name:
                CAPABILITY_DISPLAY_INFO[capabilityKey as CapabilityKey]?.name ||
                'Nexset Detection Mode',
              description:
                capabilitiesConfig?.multi_schema?.description ||
                CAPABILITY_DISPLAY_INFO[capabilityKey as CapabilityKey]?.description ||
                'Choose how many Nexsets (datasets) you want: "Single" puts all your data into one Nexset, while "Multi" creates a separate Nexset for each type of object you select.',
              type: 'string',
              required: true,
              inputType: 'select',
              order: 2,
              resource_type: 'SOURCE',

              param_type: 'single_multi_schema_selector',
              default: defaultValue,
              is_base_feed_param: false,
              is_common_param: true,
              is_root_level_param: true,
              conditionalDisplay: true,
              displayOn: [
                {
                  name: 'ui.flow_type',
                  value: 'streaming',
                },
              ],
              options: [
                {
                  label: 'Single Nexset (all feeds combined)',
                  value: 'true',
                  description: 'Create a single Nexset that combines all feeds  together',
                },
                {
                  label: 'Multi Nexset (separate per feed)',
                  value: 'false',
                  description: 'Create a separate Nexset for each individual feed',
                },
              ],
              values: [
                {
                  label: 'Single Nexset (all feeds combined)',
                  value: 'true',
                  description: 'Create a single Nexset that combines all feeds  together',
                },
                {
                  label: 'Multi Nexset (separate per feed)',
                  value: 'false',
                  description: 'Create a separate Nexset for each individual feed',
                },
              ],
            },
          ];
          break;
        case 'OBJECT_DETECTION':
          paramName = capabilitiesConfig?.schema_discovery?.param_name;
          // Normalize the default value to string "true" or "false", defaulting to "false" for any non-true value
          defaultValue =
            capabilitiesConfig?.schema_discovery?.default === true ||
            capabilitiesConfig?.schema_discovery?.default === 'true'
              ? 'true'
              : 'false';

          params = [
            {
              name: paramName,
              display_name:
                CAPABILITY_DISPLAY_INFO[capabilityKey as CapabilityKey]?.name ||
                'Object Discovery Mode',
              description:
                capabilitiesConfig?.schema_discovery?.description ||
                CAPABILITY_DISPLAY_INFO[capabilityKey as CapabilityKey]?.description ||
                'Enable automatic detection of new objects',
              type: 'string',
              required: true,
              inputType: 'select',
              order: 1,
              resource_type: 'SOURCE',
              param_type: 'object_detection_mode_selector',
              default: defaultValue,
              is_base_feed_param: false,
              is_common_param: true,
              is_root_level_param: true,
              conditionalDisplay: true,
              displayOn: [
                {
                  name: 'ui.flow_type',
                  value: 'elt',
                },
              ],

              options: [
                {
                  label: 'Automatically Detect New Objects',
                  description:
                    'Choose this option if you want the platform to auto-detect and ingest new objects.',
                  value: 'true',
                },
                {
                  label: 'Only Ingest Selected Objects',
                  description:
                    'Choose this option if do not want new objects to automatically be detected and ingested',
                  value: 'false',
                },
              ],
              values: [
                {
                  label: 'Automatically Detect New Objects',
                  description:
                    'Choose this option if you want the platform to auto-detect and ingest new objects.',
                  value: 'true',
                },
                {
                  label: 'Only Ingest Selected Objects',
                  description:
                    'Choose this option if do not want new objects to automatically be detected and ingested',
                  value: 'false',
                },
              ],
            },
          ];
          break;
        case 'FIELD_SELECTION':
          // FIELD_SELECTION has two parameters: field_list_selector and field_list
          const fieldListSelectorParam =
            capabilitiesConfig?.field_selection?.field_list_selector_param;
          const fieldListParam = capabilitiesConfig?.field_selection?.field_list_param;
          defaultValue = capabilitiesConfig?.field_selection?.default;

          // Create both parameters
          const fieldListSelector = fieldListSelectorParam
            ? {
                name: fieldListSelectorParam,
                display_name: 'Field List Selector',
                config: {
                  param_type: 'field_list_selector',
                  default: defaultValue,
                },
              }
            : null;

          const fieldList = fieldListParam
            ? {
                name: fieldListParam,
                display_name: 'Field List',
                config: {
                  param_type: 'field_list',
                  default: defaultValue,
                },
              }
            : null;

          // Combine both parameters
          params = [fieldListSelector, fieldList].filter(Boolean);
          paramName = fieldListSelectorParam; // Use the selector param as the primary param name
          break;
        case 'LOOKBACK_TIME':
          paramName = capabilitiesConfig?.lookback_time?.param_name;
          defaultValue = capabilitiesConfig?.lookback_time?.default;
          params = [
            {
              name: paramName,
              display_name:
                CAPABILITY_DISPLAY_INFO[capabilityKey as CapabilityKey]?.name || 'Lookback Time',
              description:
                capabilitiesConfig?.lookback_time?.description ||
                CAPABILITY_DISPLAY_INFO[capabilityKey as CapabilityKey]?.description ||
                'How far back to look for data',
              data_type: 'string',
              order: 3,
              resource_type: 'SOURCE',
              config: {
                default: defaultValue,
                is_base_feed_param: false,
                is_common_param: true,
              },
            },
          ];
          break;
        case 'FEED_STRATEGIES':
          // Feed strategies are controlled by the feed_strategy_mapping parameter
          // Find the actual parameter from resource_parameters
          const feedStrategyParam = resourceParams.find(
            (param) => param.config?.param_type === 'feed_strategy_mapping',
          );
          paramName = feedStrategyParam?.name;
          defaultValue = capabilitiesConfig?.feed_strategies?.default;
          params = [feedStrategyParam]; // Use the actual parameter object

          break;
      }

      capabilities[capabilityKey] = {
        enabled: true,
        param_name: paramName,
        default: defaultValue,
        params, // Convert single param to array
        variant: 'v2', // Indicate this is V2 variant
        // Add available strategies for FEED_STRATEGIES
        ...(capabilityKey === 'FEED_STRATEGIES' && {
          available_strategies: CapabilityChecker.getAvailableStrategies(
            capabilityKey as CapabilityKey,
            'v2',
            capabilitiesConfig,
          ),
        }),
      };
    } else {
      capabilities[capabilityKey] = {
        enabled: false,
        params: [], // Empty params array for disabled capabilities
        variant: 'v2', // Indicate this is V2 variant
        // Even if not enabled, FEED_STRATEGIES should still have available strategies
        ...(capabilityKey === 'FEED_STRATEGIES' && {
          available_strategies: CapabilityChecker.getAvailableStrategies(
            capabilityKey as CapabilityKey,
            'v2',
            capabilitiesConfig,
          ),
        }),
      };
    }
  });

  return capabilities;
};

/**
 * Extract additional (non-capability) parameters from V1 resource_params
 * These are parameters that need to be rendered by ResourceParamFields but aren't part of capability decision-making
 */
export const extractV1AdditionalParams = (resourceParams: any[]): any[] => {
  const capabilityParamNames = new Set<string>();

  // Collect all parameter names that are used for capabilities
  Object.keys(ELT_CAPABILITIES).forEach((capabilityKey) => {
    const capability = ELT_CAPABILITIES[capabilityKey as CapabilityKey];
    const isEnabled = CapabilityChecker.isV1CapabilityEnabled(
      capabilityKey as CapabilityKey,
      resourceParams,
    );

    if (isEnabled) {
      if (capabilityKey === 'FIELD_SELECTION') {
        // FIELD_SELECTION has multiple parameters in V1
        const fieldListSelectorParam = resourceParams.find(
          (param) => param.config?.param_type === 'field_list_selector',
        );
        const fieldListParam = resourceParams.find(
          (param) => param.config?.param_type === 'field_list',
        );

        if (fieldListSelectorParam?.name) {
          capabilityParamNames.add(fieldListSelectorParam.name);
        }
        if (fieldListParam?.name) {
          capabilityParamNames.add(fieldListParam.name);
        }
      } else {
        // Single parameter for other capabilities
        const controllingParam = resourceParams.find(
          (param) =>
            param.config?.param_type === capability.v1.paramType ||
            capability.v1.paramNamePattern.test(param.name),
        );

        if (controllingParam?.name) {
          capabilityParamNames.add(controllingParam.name);
        }
      }
    }
  });

  // Return parameters that are NOT used for capabilities
  return resourceParams.filter((param) => !capabilityParamNames.has(param.name));
};

/**
 * Extract additional (non-capability) parameters from V2 capabilities
 * For V2, we should include ALL resource_parameters as additional_params
 * unless they are specifically used for capabilities
 */
export const extractV2AdditionalParams = (
  sourceConfigById: any[] = [],
  capabilities: any = {},
): any[] => {
  // Extract parameter names from the already-processed capabilities
  const capabilityParamNames = new Set<string>();

  // Go through each capability and collect the param names
  Object.values(capabilities).forEach((capability: any) => {
    // Check params array for parameter names
    if (capability?.params && Array.isArray(capability.params)) {
      capability.params.forEach((param: any) => {
        if (param?.name) {
          capabilityParamNames.add(param.name);
        }
      });
    }
  });

  // Return parameters that are NOT used for capabilities
  // Also filter out vendor_endpoint_id and other system parameters
  const additionalParams = sourceConfigById.filter(
    (param) =>
      !capabilityParamNames.has(param.name) &&
      param.name !== 'vendor_endpoint_id' &&
      param.is_common_param !== false, // Include common parameters
  );

  return additionalParams;
};

// Capability display information
export const CAPABILITY_DISPLAY_INFO = {
  MULTI_SCHEMA: {
    name: 'Nexset Detection Mode',
    description:
      'Choose how many Nexsets (datasets) you want: "Single" puts all your data into one Nexset, while "Multi" creates a separate Nexset for each type of object you select.',
    section: 'Schema Configuration',
  },
  OBJECT_DETECTION: {
    name: 'Object Discovery Mode',
    description: 'Enable automatic detection of new objects',
    section: 'Schema Configuration',
  },
  FIELD_SELECTION: {
    name: 'Field Selection',
    description: 'Select which fields to include',
    section: 'Field Configuration',
  },
  LOOKBACK_TIME: {
    name: 'Lookback Time',
    description: 'Choose the earliest date to start loading data from the source system',
    section: 'Scheduling',
  },
  FEED_STRATEGIES: {
    name: 'Feed Strategies',
    description: 'Available sync strategies',
    section: 'Strategy Configuration',
  },
} as const;
